import type { AppUser, DSUser } from '@/types'
import type { Pinia } from 'pinia'
import converter from '@/composables/db/users/userConverter'
import createService from '@/composables/db/useServiceFactory'
import logger from '@/utils/logger'
import { serverTimestamp } from 'firebase/firestore'
import {
  getCurrentUser,
  useIsCurrentUserLoaded,
} from 'vuefire'

function getUserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch (error: unknown) {
    logger(error)
    return 'Asia/Taipei'
  }
}

export const useUserStore = defineStore('user', () => {
  const userTable = 'users'
  const isUserLoaded = useIsCurrentUserLoaded()
  const { getAvatarImageUrl } = useProfile()

  const user = ref<DSUser | null>(null)
  const userId = ref<string>('')
  const avatarImageUrl = ref<string>('')
  const userTimezone = ref<string>('')

  const checkEmailHasBeenSet = async (user: DSUser, email: string) => {
    if (user.email === email)
      return
    const userService = createService<DSUser>(userTable, converter())
    await userService.update(user.id, { email, updated: serverTimestamp() })
  }

  const initUser = () => {
    userTimezone.value = getUserTimezone()
  }

  const clearUser = () => {
    user.value = null
    userId.value = ''
  }

  const setUser = async (loggedInUser: AppUser | null) => {
    initUser()
    if (!loggedInUser) {
      clearUser()
      return null
    }
    userId.value = loggedInUser.uid
    const userService = createService<DSUser>(userTable, converter())
    user.value = await userService.get(loggedInUser.uid)
    avatarImageUrl.value = await getAvatarImageUrl(user.value as DSUser)

    if (loggedInUser.email)
      await checkEmailHasBeenSet(user.value!, loggedInUser.email)

    return user.value as DSUser
  }

  const refreshUser = async () => {
    if (userId.value === '')
      return
    const userService = createService<DSUser>(userTable, converter())
    user.value = await userService.get(userId.value)
  }

  const isUserLoggedIn = computed(() => {
    return isUserLoaded.value && userId.value !== ''
  })

  return {
    user,
    userId,
    userTimezone,
    avatarImageUrl,
    setUser,
    clearUser,
    isUserLoaded,
    isUserLoggedIn,
    getCurrentUser,
    refreshUser,
    initUser,
  }
})

export default function (pinia?: Pinia) {
  const innerStore = useUserStore(pinia)

  if (import.meta.hot)
    import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot))

  return innerStore
}
